
import { Component, VModel, Watch, Prop } from 'vue-property-decorator';
import Page from '@/Page.vue';
import {
    PlanItemModel,
    TenantUserModel,
    ProcessTypeEnum,
    AuditEscalationGroupModel,
    EscalationGroupTypeEnum,
    GroupModel,
} from '@/libs/Api';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import moment from 'moment/moment';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Numpad from '@/libs/common/components/Numpad.vue';
import KeyInput from '@/libs/common/components/KeyInput.vue';
import { auditEscalationGroupsStore } from '@/libs/audit-escalation-groups/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { groupsStore } from '@/libs/groups/+state/store';

@Component({
    components: { KeyInput, Numpad },
})
export default class TakeSolvingItem extends Page {
    @VModel() itemId!: string | null | undefined;
    @Prop() take!: () => void;
    private auditEscalationGroups: AuditEscalationGroupModel[] | undefined = undefined;
    protected tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    protected isLoading = false;
    protected isTaking = false;
    protected takeDialog = false;

    @Watch('takeDialog')
    takeDialogChanged(v: boolean) {
        if (!v && this.itemId) {
            this.itemId = null;
        }
    }

    @Watch('itemId')
    async itemIdChanged(v: string | null | undefined) {
        this.takeDialog = !!v;
        if (v) {
            this.isLoading = true;
            planItemsStore.dispatch(CrudAction.Get, { id: this.itemId });
            await this.subscribe(planItemsStore, CrudReponse.Get);
            const planItem = planItemsStore.useGetter(CrudGetter.Detail) as PlanItemModel;
            await this.loadEscalationGroups(planItem.auditId!);
            this.isLoading = false;
        }
    }

    protected canTake() {
        const planItem: PlanItemModel | null | undefined = planItemsStore.useGetter(CrudGetter.Detail);
        const groups: GroupModel[] = groupsStore.useGetter(CrudGetter.Data) ?? [];
        const myGroupIds = groups
            .filter((x) => x.groupUsers?.some((n) => n.tenantUserId == this.tenantUser.id))
            .map((x) => x.id!);
        return (
            planItem &&
            planItem.audit &&
            planItem.audit.category &&
            planItem.audit.category.processType == ProcessTypeEnum.IssueSolving &&
            this.auditEscalationGroups?.some((x) => x.auditId == planItem!.auditId && myGroupIds.includes(x.groupId!))
        );
    }

    get item(): PlanItemModel {
        return planItemsStore.useGetter(CrudGetter.Detail);
    }

    loadEscalationGroups(auditId: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            auditEscalationGroupsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<AuditEscalationGroupModel>(
                    undefined,
                    [
                        { field: 'auditId', op: 'eq', comparand: auditId },
                        { field: 'escalationGroupType', op: 'eq', comparand: EscalationGroupTypeEnum.IssueEscalation },
                    ],
                    undefined,
                    false,
                    'TakeSolvingItem'
                )
            );
            this.subscribe(auditEscalationGroupsStore, CrudReponse.GetAll, 'TakeSolvingItem')
                .then((e: AuditEscalationGroupModel[]) => {
                    this.auditEscalationGroups = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    mounted() {
        this.itemIdChanged(this.itemId);
    }

    onTake() {
        this.isTaking = true;
        planItemsStore.dispatch(CrudAction.Get, { id: this.itemId });
        this.subscribe(planItemsStore, CrudReponse.Get).then((e: PlanItemModel) => {
            let maxSequenceIndex: number = e
                .performations!.map((x) => x.sequenceIndex ?? 0)
                .reduce((a: number, b: number) => Math.max(a, b), 0);
            e.performations!.forEach((x) => {
                if (x.toDate == null) {
                    x.toDate = moment().format('YYYY-MM-DDTHH:mm:ss');
                }
            });
            e.performations!.push({
                fromDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
                tenantUserId: this.tenantUser.id,
                sequenceIndex: ++maxSequenceIndex,
            });
            e.tenantUserId = this.tenantUser.id;
            planItemsStore.dispatch(CrudAction.Update, { item: e });
            this.subscribe(planItemsStore, CrudReponse.Update).then((e: PlanItemModel) => {
                if (this.take) {
                    this.take();
                } else {
                    if (this.$router.currentRoute.name == 'WorkTaskAuditFormExisting') {
                        window.location.reload();
                    } else {
                        this.$router.push({ name: 'WorkTaskAuditFormExisting', params: { id: e.id! } });
                    }
                }
                this.isTaking = false;
                this.onClose();
            });
        });
    }

    onClose() {
        this.$emit('input', null);
    }
}
